import type { AgentToolDescriptor } from "./agentTypes";
import type { ToolResult } from "./tools";

type Row = Record<string, unknown>;

const SCOPE_TOGGLES = ["includeProducts", "includeMinimumRequirements", "includeAwardCriteria"];
const NOT_IN_A_PRIVATE_COMPETITION = ["contractDuration", "options", "milestonePlan"];

export const HIDDEN_EVENT_DATA_BY_CONTEXT: Record<string, readonly string[]> = {
  Public: SCOPE_TOGGLES,
  MiniCompetition: SCOPE_TOGGLES,
  Private: NOT_IN_A_PRIVATE_COMPETITION,
};

const FIELD_NAMES: Record<string, string> = {
  contractDuration: "kontraktsperiode",
  options: "opsjoner",
  milestonePlan: "spørsmålsfrist og fremdriftsplan",
  includeProducts: "valget om produkter skal være med",
  includeMinimumRequirements: "valget om minstekrav skal være med",
  includeAwardCriteria: "valget om tildelingskriterier skal være med",
};

export const hiddenEventDataOf = (event: unknown): readonly string[] =>
  HIDDEN_EVENT_DATA_BY_CONTEXT[contextTypeOf(event)] ?? [];

export type Withheld = { fields: string[]; refusal: ToolResult };

export const withheldFieldsOf = (descriptor: AgentToolDescriptor, args: Row, current: unknown): Withheld | null => {
  const hidden = proposedOf(descriptor, args, hiddenEventDataOf(current));
  if (hidden.length > 0) return { fields: hidden, refusal: hiddenFieldsRefusal(hidden) };
  if (contextTypeOf(current) !== "Unclassified") return null;
  const awaiting = proposedOf(descriptor, args, NOT_IN_A_PRIVATE_COMPETITION);
  return awaiting.length > 0 ? { fields: awaiting, refusal: awaitingRegulationRefusal(awaiting) } : null;
};

export const withoutHiddenFields = (descriptor: AgentToolDescriptor, args: Row, hidden: string[]): Row => {
  const body = bodyName(descriptor);
  const patch = patchOf(descriptor, args);
  if (!body || !patch || !isRow(patch.eventData)) return args;
  const eventData = Object.fromEntries(Object.entries(patch.eventData).filter(([key]) => !hidden.includes(key)));
  const claims = Array.isArray(patch.fieldSources)
    ? patch.fieldSources.filter((claim) => !(isRow(claim) && typeof claim.path === "string" && claimsHidden(claim.path, hidden)))
    : patch.fieldSources;
  return {
    ...args,
    [body]: { ...patch, eventData, ...(claims !== undefined ? { fieldSources: claims } : {}) },
  };
};

const NEVER_MISSING =
  "Si aldri at de mangler, ikke ble registrert eller gjenstår, og aldri at et område er uferdig på grunn av dem: " +
  "de hører ikke til noe område i intervjuet.";

const hiddenFieldsRefusal = (hidden: string[]): ToolResult => ({
  ok: false,
  deviationCode: "NOT_ON_THIS_WIZARD",
  message:
    `Ikke vist brukeren: ${hidden.map((key) => FIELD_NAMES[key] ?? "et felt veiviseren ikke viser").join(", ")} ` +
    "vises ikke i veiviseren for denne anskaffelsen. Foreslå det samme på nytt uten disse feltene, og nevn dem ikke " +
    "i oppsummeringen eller svaret. " +
    NEVER_MISSING,
});

const awaitingRegulationRefusal = (fields: string[]): ToolResult => ({
  ok: false,
  deviationCode: "NOT_BEFORE_REGULATION",
  message:
    `Ikke vist brukeren: ${fields.map((key) => FIELD_NAMES[key] ?? "et felt").join(", ")} kan ikke foreslås før ` +
    "regelverket for anskaffelsen er bekreftet, fordi veiviseren for en privat konkurranse ikke har felt for dem. " +
    "Foreslå resten på nytt uten disse feltene, og nevn dem ikke i oppsummeringen eller svaret. Når regelverket er " +
    "bekreftet, tar du dem opp bare hvis anskaffelsen ikke er en privat konkurranse. " +
    NEVER_MISSING,
});

export const savedWithoutNote = (fields: string[]): string =>
  `Lagret uten ${fields.map((key) => FIELD_NAMES[key] ?? "et felt").join(", ")}: brukeren så dem ikke på kortet, ` +
  "fordi veiviseren ikke har felt for dem nå. Nevn dem ikke i svaret. " +
  NEVER_MISSING;

const proposedOf = (descriptor: AgentToolDescriptor, args: Row, keys: readonly string[]): string[] => {
  const eventData = patchOf(descriptor, args)?.eventData;
  if (!isRow(eventData)) return [];
  return keys.filter((key) => eventData[key] !== undefined);
};

const claimsHidden = (path: string, hidden: string[]): boolean =>
  hidden.some((key) => path === `eventData.${key}` || path.startsWith(`eventData.${key}.`) || path.startsWith(`eventData.${key}[`));

const contextTypeOf = (event: unknown): string => {
  const context = isRow(event) ? event.eventContext : undefined;
  return isRow(context) && typeof context.type === "string" ? context.type : "";
};

const bodyName = (descriptor: AgentToolDescriptor): string | undefined =>
  descriptor.params.find((param) => param.in === "body")?.name;

const patchOf = (descriptor: AgentToolDescriptor, args: Row): Row | undefined => {
  const body = bodyName(descriptor);
  const patch = body ? args[body] : undefined;
  return isRow(patch) ? patch : undefined;
};

const isRow = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);
